import type { LoginOutput } from '@allocab/allocab-commons-shared-types';
import { storage } from '@frontend/shared';

import { constants } from '../constants';
import type { ApiErrorShape } from './apiError';

/**
 * Télécharge un fichier authentifié depuis une route relative (facture PDF,
 * exports CSV). Passe par `fetch` et non par le client axios partagé, dont
 * l'intercepteur déballe l'enveloppe JSON et ne sait pas rendre un binaire.
 *
 * En cas d'échec, l'erreur est rejetée **dans la forme du client partagé**
 * (`statusCode`, `data.errorMessage`, `message`) pour que `toUserErrorMessage`
 * la traite comme n'importe quelle autre : un 404 sur un PDF de facture ou un
 * 403 sur un export donnent alors le même message que partout ailleurs, au lieu
 * d'un « HTTP 404 » brut.
 *
 * Note : sur un export refusé le back détaille la permission manquante
 * (`extranet-exports`). On garde volontairement notre libellé : c'est un nom
 * de permission interne, sans valeur pour un utilisateur client. Le détail
 * reste lisible dans l'onglet réseau pour le support.
 */
export const downloadFile = async (path: string, filename: string): Promise<void> => {
  const tokens = storage.getJSONItem<LoginOutput>('tokens');
  const accessToken = tokens?.accessToken;

  const baseUrl = constants.extranetApi || '';
  const url = path.startsWith('http') ? path : `${baseUrl}${path}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
  });

  if (!response.ok) {
    throw await toApiError(response);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Libère la mémoire après un délai (le download a démarré)
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

/** Reconstruit l'enveloppe d'erreur du back quand elle est disponible. */
const toApiError = async (response: Response): Promise<ApiErrorShape> => {
  const error: ApiErrorShape = { statusCode: response.status };
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      const body = (await response.json()) as { data?: { errorMessage?: string }; message?: string };
      error.data = body.data;
      error.message = body.message;
    } catch {
      // Corps illisible : le statut suffit à produire un message correct.
    }
  }
  return error;
};
